import type { ReactNode } from 'react';

import { Checkbox, Divider, FlexAligner, Typography } from '@scaleaq/scaleui';
import { AddOrUpdatePresetRequest, CameraInfo } from '@scaleaq/sitecontrol-lib';
import { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCameraChanged } from '@/components/cameraStreamWidget/useCameraChanged.ts';
import { useNumberFormat } from '@/localization';
import { responsive } from '@/utils';

import { PositionEntry } from './presetPositionFields/PositionEntry.tsx';
import {
  degreeFormat,
  measurementUnitSymbol,
  meterFormat,
} from './presetPositionFields/presetPositionFields.functions.ts';
import {
  StyledColumnHeader,
  StyledPositionGrid,
} from './presetPositionFields/presetPositionFields.styles.ts';
import { usePresetAxis } from './usePresetAxis.ts';

type PresetPositionFieldsProps = {
  camera: CameraInfo;
  control: Control<AddOrUpdatePresetRequest>;
  hasSmartWinch: boolean;
  includeOrientation: boolean;
  onIncludeOrientationChange: (includeOrientation: boolean) => void;
  setValue: UseFormSetValue<AddOrUpdatePresetRequest>;
};

type MeasurementGridProps = {
  children: ReactNode;
};

export const PresetPositionFields = ({
  camera,
  control,
  hasSmartWinch,
  includeOrientation,
  onIncludeOrientationChange,
  setValue,
}: PresetPositionFieldsProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['cameraControl', 'common']);
  const { formatNumber } = useNumberFormat();
  // `camera` is a one-off REST fetch. Winch and gimbal positions arrive on `CameraChanged`.
  const liveCamera = useCameraChanged(camera.id);
  const winchInfo = liveCamera?.winchInfo ?? camera.winchInfo;
  const orientation = liveCamera?.orientation ?? camera.orientation;
  const depth = usePresetAxis({ actual: winchInfo?.position.depth, name: 'depth', setValue });
  const distance = usePresetAxis({
    actual: winchInfo?.position.distance,
    name: 'distance',
    setValue,
  });
  const pan = usePresetAxis({
    actual: orientation?.pan,
    enabled: includeOrientation,
    name: 'pan',
    setValue,
  });
  const tilt = usePresetAxis({
    actual: orientation?.tilt,
    enabled: includeOrientation,
    name: 'tilt',
    setValue,
  });
  const meterSuffix = measurementUnitSymbol({ language, options: meterFormat });
  const degreeSuffix = measurementUnitSymbol({ language, options: degreeFormat });
  const depthActualText =
    depth.actualValue === undefined ? '' : formatNumber(depth.actualValue, meterFormat);
  const distanceActualText =
    distance.actualValue === undefined ? '' : formatNumber(distance.actualValue, meterFormat);
  const panActualText =
    pan.actualValue === undefined ? '' : formatNumber(pan.actualValue, degreeFormat);
  const tiltActualText =
    tilt.actualValue === undefined ? '' : formatNumber(tilt.actualValue, degreeFormat);

  return (
    <FlexAligner direction='column' gap={responsive.spacing['3xlarge']}>
      {hasSmartWinch && (
        <>
          <MeasurementGrid>
            <PositionEntry
              label={t('preset.depth')}
              name='depth'
              control={control}
              decimals={2}
              unitSuffix={meterSuffix}
              actualText={depthActualText || '--'}
              actualAriaLabel={t('preset.actualDepth', {
                value: depthActualText || t('common:notAvailable'),
              })}
              applyLabel={t('preset.applyDepth')}
              applyDisabled={depth.actualValue === undefined}
              onApply={depth.applyActual}
            />
            <PositionEntry
              label={t('preset.distance')}
              name='distance'
              control={control}
              decimals={2}
              unitSuffix={meterSuffix}
              actualText={distanceActualText || '--'}
              actualAriaLabel={t('preset.actualDistance', {
                value: distanceActualText || t('common:notAvailable'),
              })}
              applyLabel={t('preset.applyDistance')}
              applyDisabled={distance.actualValue === undefined}
              onApply={distance.applyActual}
            />
          </MeasurementGrid>
          <Divider />
          <Checkbox
            label={t('preset.controlPanAndTilt')}
            labelPosition='right'
            checked={includeOrientation}
            onChange={onIncludeOrientationChange}
          />
        </>
      )}
      {includeOrientation && (
        <MeasurementGrid>
          <PositionEntry
            label={t('preset.pan')}
            name='pan'
            control={control}
            decimals={2}
            unitSuffix={degreeSuffix}
            actualText={panActualText || t('common:notAvailable')}
            actualAriaLabel={t('preset.actualPan', {
              value: panActualText || t('common:notAvailable'),
            })}
            applyLabel={t('preset.applyPan')}
            applyDisabled={pan.actualValue === undefined}
            onApply={pan.applyActual}
          />
          <PositionEntry
            label={t('preset.tilt')}
            name='tilt'
            control={control}
            decimals={2}
            unitSuffix={degreeSuffix}
            actualText={tiltActualText || t('common:notAvailable')}
            actualAriaLabel={t('preset.actualTilt', {
              value: tiltActualText || t('common:notAvailable'),
            })}
            applyLabel={t('preset.applyTilt')}
            applyDisabled={tilt.actualValue === undefined}
            onApply={tilt.applyActual}
          />
        </MeasurementGrid>
      )}
    </FlexAligner>
  );
};

const MeasurementGrid = ({ children }: MeasurementGridProps) => {
  const { t } = useTranslation('cameraControl');

  return (
    <StyledPositionGrid>
      {/* Fills the label and input columns so the heading stays on the first row. */}
      <span />
      <span />
      <StyledColumnHeader>
        <Typography type='body_medium' weight={600}>
          {t('preset.currentData')}
        </Typography>
      </StyledColumnHeader>
      {children}
    </StyledPositionGrid>
  );
};
